import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';

import { useAppDispatch, useAppSelector } from '@/features/hooks';
import { useFABContext } from '@/components/common/FABContext';
import { fetchAccounts } from '@/features/accounts/accountsSlice';
import {
  fetchSubscriptions,
  createSubscription,
  updateSubscription,
  deleteSubscription,
  pauseSubscription,
  resumeSubscription,
  cancelSubscription,
  confirmPayment,
  fetchSubscriptionSummary,
  fetchSubscriptionCalendar,
} from '@/features/subscriptions/subscriptionsSlice';
import { useCurrency } from '@/hooks/useCurrency';
import { useToast } from '@/hooks/use-toast';
import { ToastAction } from '@/components/ui/toast';

import {
  Plus,
  MoreVertical,
  Pause,
  Play,
  X,
  Trash2,
  RefreshCw,
  Pencil,
  CreditCard,
  ExternalLink,
  Search,
  AlertTriangle,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  ArrowRight,
  CalendarDays,
} from 'lucide-react';
import type { Subscription } from '@/lib/types';
import { SubscriptionForm, type SubscriptionFormValues } from '@/components/subscriptions/SubscriptionForm';
import SubscriptionDetailSheet from '@/components/subscriptions/SubscriptionDetailSheet';
import SubscriptionCalendar from '@/components/subscriptions/SubscriptionCalendar';
import TransactionPreviewDialog from '@/components/subscriptions/TransactionPreviewDialog';
import { CatchUpBillingWizard } from '@/components/subscriptions/CatchUpBillingWizard';
import api, { endpoints } from '@/services/api';
import PageGuide from '@/components/common/PageGuide';
import { subscriptionsGuide } from '@/components/common/guideContent';
import { IconButton } from '@/components/ui/icon-button';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

const statusColors: Record<string, string> = {
  active: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  paused: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  cancelled: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  expired: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300',
  detected: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
};

const statusTopColors: Record<string, string> = {
  active: 'border-t-green-500',
  paused: 'border-t-yellow-500',
  cancelled: 'border-t-red-500',
  expired: 'border-t-gray-400',
  detected: 'border-t-blue-500',
};

type StatusFilter = 'all' | 'active' | 'paused' | 'detected' | 'cancelled';
type SortField = 'merchantName' | 'amount' | 'nextBillingDate';
type SortDirection = 'asc' | 'desc';

export default function Subscriptions() {
  useDocumentTitle('Subscriptions');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { fmt } = useCurrency();
  const { toast } = useToast();
  const { subscriptions, isLoading, summary } = useAppSelector((state) => state.subscriptions);

  const [formOpen, setFormOpen] = useState(false);
  const [editingSub, setEditingSub] = useState<Subscription | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [subToDelete, setSubToDelete] = useState<Subscription | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('list');

  const [detailSheetOpen, setDetailSheetOpen] = useState(false);
  const [detailSub, setDetailSub] = useState<Subscription | null>(null);

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [sortField, setSortField] = useState<SortField>('merchantName');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkCancelOpen, setBulkCancelOpen] = useState(false);
  const [previewSubId, setPreviewSubId] = useState<string | null>(null);
  const [catchUpState, setCatchUpState] = useState<{
    subscriptionId: string;
    subscriptionName: string;
    missedCycles: Array<{ date: string; amount: number; currency: string }>;
    truncated: boolean;
  } | null>(null);

  const { registerFABActions } = useFABContext();
  useEffect(() => {
    registerFABActions({ onAddSubscription: () => { setEditingSub(null); setFormOpen(true); } });
    return () => registerFABActions({});
  }, [registerFABActions]);

  const location = useLocation();
  useEffect(() => {
    if ((location.state as { openForm?: boolean } | null)?.openForm) {
      setEditingSub(null);
      setFormOpen(true);
      navigate(location.pathname + location.search, { replace: true, state: null });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const hasFetched = useRef(false);
  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    dispatch(fetchSubscriptions(undefined));
    dispatch(fetchSubscriptionSummary());
    dispatch(fetchAccounts());
  }, [dispatch]);

  const openDetail = (sub: Subscription) => {
    setDetailSub(sub);
    setDetailSheetOpen(true);
  };

  const filteredSubscriptions = useMemo(() => {
    let result = subscriptions;
    if (statusFilter !== 'all') {
      result = result.filter((s) => s.status === statusFilter);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter((s) => s.merchantName.toLowerCase().includes(q));
    }
    return result;
  }, [subscriptions, searchQuery, statusFilter]);

  const sortedSubscriptions = useMemo(() => {
    const sorted = [...filteredSubscriptions];
    sorted.sort((a, b) => {
      let cmp = 0;
      if (sortField === 'merchantName') {
        cmp = a.merchantName.localeCompare(b.merchantName);
      } else if (sortField === 'amount') {
        cmp = a.amount - b.amount;
      } else if (sortField === 'nextBillingDate') {
        const aDate = a.nextBillingDate ? new Date(a.nextBillingDate).getTime() : Infinity;
        const bDate = b.nextBillingDate ? new Date(b.nextBillingDate).getTime() : Infinity;
        cmp = aDate - bDate;
      }
      return sortDirection === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }, [filteredSubscriptions, sortField, sortDirection]);

  const statusCounts = useMemo(() => ({
    all: subscriptions.length,
    active: subscriptions.filter((s) => s.status === 'active').length,
    paused: subscriptions.filter((s) => s.status === 'paused').length,
    detected: subscriptions.filter((s) => s.status === 'detected').length,
    cancelled: subscriptions.filter((s) => s.status === 'cancelled').length,
  }), [subscriptions]);

  const activeSubscriptions = subscriptions.filter(s => s.status === 'active');

  const monthlyTotal = useMemo(() => {
    return activeSubscriptions.reduce((sum, s) => {
      const multiplier: Record<string, number> = {
        weekly: 4.33,
        biweekly: 2.17,
        monthly: 1,
        quarterly: 0.33,
        yearly: 0.083,
        custom: s.customIntervalDays ? 30 / s.customIntervalDays : 1,
      };
      return sum + s.amount * (multiplier[s.frequency] || 1);
    }, 0);
  }, [activeSubscriptions]);

  const nextRenewal = useMemo(() => {
    const now = new Date();
    let soonest: Subscription | null = null;
    let soonestDate = Infinity;
    for (const s of activeSubscriptions) {
      if (s.nextBillingDate) {
        const d = new Date(s.nextBillingDate).getTime();
        if (d >= now.getTime() && d < soonestDate) {
          soonestDate = d;
          soonest = s;
        }
      }
    }
    return soonest;
  }, [activeSubscriptions]);

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === sortedSubscriptions.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(sortedSubscriptions.map((s) => s._id)));
    }
  };

  const handleBulkPause = async () => {
    const ids = Array.from(selectedIds);
    for (const id of ids) {
      await dispatch(pauseSubscription(id));
    }
    dispatch(fetchSubscriptionSummary());
    toast({
      title: `${ids.length} subscription${ids.length > 1 ? 's' : ''} paused`,
      action: (
        <Button variant="outline" size="sm" onClick={async () => {
          for (const id of ids) {
            await handleResume(id);
          }
        }}>
          Undo
        </Button>
      ),
    });
    setSelectedIds(new Set());
  };

  const handleBulkCancel = async () => {
    const ids = Array.from(selectedIds);
    for (const id of ids) {
      await dispatch(cancelSubscription(id));
    }
    dispatch(fetchSubscriptionSummary());
    toast({ title: `${ids.length} subscription${ids.length > 1 ? 's' : ''} cancelled` });
    setSelectedIds(new Set());
    setBulkCancelOpen(false);
  };

  const handleCancel = async (id: string) => {
    const result = await dispatch(cancelSubscription(id));
    dispatch(fetchSubscriptionSummary());
    if (cancelSubscription.fulfilled.match(result)) {
      const { pendingTransactionCount } = result.payload;
      if (pendingTransactionCount > 0) {
        toast({
          title: 'Subscription cancelled',
          description: `${pendingTransactionCount} pending transaction${pendingTransactionCount > 1 ? 's' : ''} kept — no data deleted.`,
          variant: 'destructive',
          action: (
            <ToastAction altText="Review transactions" onClick={() => navigate('/transactions?status=pending')}>
              Review Transactions
            </ToastAction>
          ),
        });
      } else {
        toast({
          title: 'Subscription cancelled',
          description: 'No pending transactions affected.',
        });
      }
    }
  };

  const handleResume = async (id: string) => {
    const result = await dispatch(resumeSubscription(id));
    dispatch(fetchSubscriptionSummary());
    if (resumeSubscription.fulfilled.match(result)) {
      const { missedCycles, truncated } = result.payload;
      if (missedCycles && missedCycles.length > 0) {
        const sub = subscriptions.find(s => s._id === id);
        setCatchUpState({
          subscriptionId: id,
          subscriptionName: sub?.name || sub?.merchantName || 'Subscription',
          missedCycles,
          truncated,
        });
      } else {
        toast({ title: 'Subscription resumed', description: 'No missed billing cycles.' });
      }
    }
  };

  const handleCatchUpConfirm = async (confirmedCycles: { date: string; amount: number }[]) => {
    if (!catchUpState) return;
    await api.post(endpoints.subscriptions.processBillingConfirm(catchUpState.subscriptionId), { confirmedCycles });
    setCatchUpState(null);
    const count = confirmedCycles.length;
    toast({
      title: 'Billing catch-up complete',
      description: count > 0
        ? `${count} transaction${count > 1 ? 's' : ''} created for missed payments.`
        : 'Subscription resumed without recording missed payments.',
    });
  };

  const handleFormSubmit = async (values: SubscriptionFormValues) => {
    setIsSubmitting(true);
    try {
      const payload: Partial<Subscription> = {
        merchantName: values.merchantName,
        amount: values.amount,
        frequency: values.frequency,
        currency: values.currency,
        emoji: values.emoji || undefined,
        notes: values.notes || undefined,
        startDate: values.startDate || undefined,
        endDate: values.indefinite ? undefined : values.endDate || undefined,
        status: values.status,
        account: values.account || undefined,
        name: values.merchantName,
        historyGenerationMode: values.historyGenerationMode,
        transactionDefaults: {
          description: values.transactionDefaults?.description || undefined,
          tags: (values.transactionDefaults?.tags ?? []).length > 0
            ? values.transactionDefaults!.tags
            : undefined,
          merchant: values.transactionDefaults?.merchant || undefined,
          notes: values.transactionDefaults?.notes || undefined,
          emoji: values.transactionDefaults?.emoji || undefined,
          type: (values.transactionDefaults?.type as 'expense' | 'income') || undefined,
          needsReview: values.transactionDefaults?.needsReview || undefined,
        },
      };
      if (values.frequency === 'custom' && values.customIntervalDays) {
        payload.customIntervalDays = values.customIntervalDays as number;
      }
      if (values.billingDayOfMonth) {
        payload.billingDayOfMonth = values.billingDayOfMonth as number;
      }

      if (editingSub) {
        await dispatch(updateSubscription({ id: editingSub._id, data: payload })).unwrap();
        toast({ title: 'Subscription updated' });
      } else {
        await dispatch(createSubscription(payload)).unwrap();
        toast({ title: 'Subscription created' });
      }
      dispatch(fetchSubscriptionSummary());
      setFormOpen(false);
      setEditingSub(null);
    } catch (error: unknown) {
      toast({ title: 'Unable to save', description: (error instanceof Error ? error.message : '') || 'Please try again', variant: 'destructive' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!subToDelete) return;
    try {
      await dispatch(deleteSubscription(subToDelete._id)).unwrap();
      toast({ title: 'Subscription deleted' });
      dispatch(fetchSubscriptionSummary());
    } catch (error: unknown) {
      toast({ title: 'Unable to delete', description: (error instanceof Error ? error.message : '') || 'Please try again', variant: 'destructive' });
    } finally {
      setSubToDelete(null);
    }
  };

  const handleConfirmPayment = async (subId: string) => {
    // Check if subscription uses draft mode — show preview first
    const sub = subscriptions.find((s) => s._id === subId);
    if (sub?.historyGenerationMode === 'draft') {
      setPreviewSubId(subId);
      return;
    }
    await executeConfirmPayment(subId);
  };

  const executeConfirmPayment = async (subId: string) => {
    try {
      await dispatch(confirmPayment(subId)).unwrap();

      toast({
        title: 'Payment confirmed',
        description: 'Linked transaction was marked cleared.',
      });

      await Promise.all([
        dispatch(fetchSubscriptions(undefined)),
        dispatch(fetchSubscriptionSummary()),
        dispatch(fetchSubscriptionCalendar()),
      ]);
    } catch (error: unknown) {
      const description =
        typeof error === 'string'
          ? error
          : (error instanceof Error ? error.message : '') || 'Please try again';

      toast({
        title: 'Unable to confirm payment',
        description,
        variant: 'destructive',
      });
    }
  };

  const openTransactions = useCallback((sub: Subscription) => {
    navigate(`/transactions?subscriptionId=${sub._id}`);
  }, [navigate]);

  const frequencyLabel = (sub: Subscription) => {
    if (sub.frequency === 'custom' && sub.customIntervalDays) return `Every ${sub.customIntervalDays}d`;
    return sub.frequency.charAt(0).toUpperCase() + sub.frequency.slice(1);
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <ChevronsUpDown className="ml-1 h-3.5 w-3.5 text-muted-foreground/50" />;
    return sortDirection === 'asc'
      ? <ChevronUp className="ml-1 h-3.5 w-3.5" />
      : <ChevronDown className="ml-1 h-3.5 w-3.5" />;
  };

  const getCategoryName = (sub: Subscription): string | null => {
    const cat = sub.category;
    if (!cat) return null;
    if (typeof cat === 'object' && 'name' in (cat as Record<string, unknown>)) return (cat as { name: string }).name;
    return null;
  };

  const renderContent = () => {
    if (isLoading && subscriptions.length === 0) {
      return (
        <div className="space-y-2">
          {[...Array(4)].map((_, idx) => (
            <div key={idx} className="flex items-center gap-4 rounded-md border p-4">
              <Skeleton className="h-9 w-9 rounded-full shrink-0" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-3 w-16" />
              </div>
              <Skeleton className="h-4 w-20" />
            </div>
          ))}
        </div>
      );
    }

    if (subscriptions.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed p-8 text-center">
          <RefreshCw className="mb-3 h-8 w-8 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">No subscriptions tracked yet. Add your first subscription to get started.</p>
          <Button className="mt-4" onClick={() => { setEditingSub(null); setFormOpen(true); }}>
            <Plus className="mr-2 h-4 w-4" /> Add Subscription
          </Button>
        </div>
      );
    }

    const renderDropdown = (sub: Subscription) => (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <IconButton variant="ghost" size="icon" aria-label={`Manage ${sub.merchantName}`} tooltip="Subscription actions" onClick={(e) => e.stopPropagation()}>
            <MoreVertical className="h-4 w-4" />
          </IconButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setEditingSub(sub); setFormOpen(true); }}>
            <Pencil className="mr-2 h-4 w-4" /> Edit
          </DropdownMenuItem>
          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openTransactions(sub); }}>
            <ExternalLink className="mr-2 h-4 w-4" /> View Transactions
          </DropdownMenuItem>
          {sub.status === 'active' && (
            <>
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleConfirmPayment(sub._id); }}>
                <CreditCard className="mr-2 h-4 w-4" /> Confirm Payment
              </DropdownMenuItem>
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); dispatch(pauseSubscription(sub._id)); }}>
                <Pause className="mr-2 h-4 w-4" /> Pause
              </DropdownMenuItem>
            </>
          )}
          {sub.status === 'paused' && (
            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleResume(sub._id); }}>
              <Play className="mr-2 h-4 w-4" /> Resume
            </DropdownMenuItem>
          )}
          {sub.status !== 'cancelled' && sub.status !== 'expired' && (
            <DropdownMenuItem data-testid="cancel-sub-btn" onClick={(e) => { e.stopPropagation(); handleCancel(sub._id); }}>
              <X className="mr-2 h-4 w-4" /> Cancel
            </DropdownMenuItem>
          )}
          <DropdownMenuItem className="text-destructive" onClick={(e) => { e.stopPropagation(); setSubToDelete(sub); }}>
            <Trash2 className="mr-2 h-4 w-4" /> Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );

    return (
      <>
        {/* Bulk actions bar */}
        {selectedIds.size > 0 && (
          <div className="hidden md:flex items-center gap-3 rounded-md border bg-muted/50 p-2 mb-3">
            <span className="text-sm font-medium">{selectedIds.size} selected</span>
            <Button size="sm" variant="outline" onClick={handleBulkPause}>
              <Pause className="mr-1.5 h-3.5 w-3.5" /> Pause Selected
            </Button>
            <Button size="sm" variant="destructive" onClick={() => setBulkCancelOpen(true)}>
              <X className="mr-1.5 h-3.5 w-3.5" /> Cancel Selected
            </Button>
          </div>
        )}

        {/* Desktop table */}
        <Table className="hidden md:table">
          <TableHeader>
            <TableRow>
              <TableHead className="w-10 hidden md:table-cell">
                <Checkbox
                  checked={sortedSubscriptions.length > 0 && selectedIds.size === sortedSubscriptions.length}
                  onCheckedChange={toggleSelectAll}
                  aria-label="Select all"
                />
              </TableHead>
              <TableHead>
                <button
                  type="button"
                  className="flex items-center font-medium hover:text-foreground transition-colors"
                  onClick={() => toggleSort('merchantName')}
                >
                  Service
                  <SortIcon field="merchantName" />
                </button>
              </TableHead>
              <TableHead className="text-right">
                <button
                  type="button"
                  className="flex items-center justify-end w-full font-medium hover:text-foreground transition-colors"
                  onClick={() => toggleSort('amount')}
                >
                  Amount
                  <SortIcon field="amount" />
                </button>
              </TableHead>
              <TableHead>Frequency</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="hidden lg:table-cell">Category</TableHead>
              <TableHead>
                <button
                  type="button"
                  className="flex items-center font-medium hover:text-foreground transition-colors"
                  onClick={() => toggleSort('nextBillingDate')}
                >
                  Next Billing
                  <SortIcon field="nextBillingDate" />
                </button>
              </TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedSubscriptions.map((sub) => (
              <TableRow key={sub._id} className="cursor-pointer hover:bg-muted/50 transition-colors" onClick={() => openDetail(sub)}>
                <TableCell className="hidden md:table-cell" onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    checked={selectedIds.has(sub._id)}
                    onCheckedChange={() => toggleSelect(sub._id)}
                    aria-label={`Select ${sub.merchantName}`}
                  />
                </TableCell>
                <TableCell className="flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-lg shrink-0">
                    {sub.emoji || sub.merchantName.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div className="font-medium">{sub.merchantName}</div>
                  </div>
                </TableCell>
                <TableCell className="text-right font-semibold">{fmt(sub.amount)}</TableCell>
                <TableCell>
                  <Badge variant="secondary" className="capitalize">{frequencyLabel(sub)}</Badge>
                </TableCell>
                <TableCell>
                  <Badge className={statusColors[sub.status] || ''} variant="outline">{sub.status}</Badge>
                </TableCell>
                <TableCell className="hidden lg:table-cell">
                  {getCategoryName(sub) ? (
                    <Badge variant="secondary">{getCategoryName(sub)}</Badge>
                  ) : (
                    <span className="text-muted-foreground text-sm">—</span>
                  )}
                </TableCell>
                <TableCell>
                  {sub.nextBillingDate
                    ? new Date(sub.nextBillingDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
                    : '—'}
                </TableCell>
                <TableCell className="text-right">{renderDropdown(sub)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {/* Mobile cards — 2×2 grid */}
        <div className="md:hidden grid grid-cols-2 gap-2">
          {sortedSubscriptions.map((sub) => (
            <div
              key={sub._id}
              data-testid={`subscription-card-${sub._id}`}
              className={`flex flex-col gap-1.5 p-3 rounded-lg border border-t-[3px] ${statusTopColors[sub.status] || 'border-t-gray-300'} bg-card cursor-pointer hover:bg-muted/50 transition-colors min-h-[44px] active:scale-[0.98]`}
              onClick={() => openDetail(sub)}
            >
              {/* Header: emoji + actions */}
              <div className="flex items-start justify-between gap-1">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-base">
                  {sub.emoji || sub.merchantName.charAt(0).toUpperCase()}
                </div>
                <div onClick={(e) => e.stopPropagation()}>
                  {renderDropdown(sub)}
                </div>
              </div>
              {/* Name */}
              <p className="font-medium text-sm leading-tight break-words line-clamp-2">{sub.merchantName}</p>
              {/* Amount */}
              <p className="font-bold text-sm">{fmt(sub.amount)}</p>
              {/* Status + frequency row */}
              <div className="flex flex-wrap items-center gap-1">
                <Badge className={`${statusColors[sub.status] || ''} text-[10px] px-1.5 py-0`} variant="outline">
                  {sub.status}
                </Badge>
                <span className="text-[10px] text-muted-foreground capitalize">{frequencyLabel(sub)}</span>
              </div>
              {/* Next billing date */}
              {sub.nextBillingDate && (
                <p className="text-[10px] text-muted-foreground">
                  {new Date(sub.nextBillingDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                </p>
              )}
            </div>
          ))}
        </div>
      </>
    );
  };

  return (
    <div className="space-y-4 md:space-y-8">
      <PageGuide pageKey="subscriptions" content={subscriptionsGuide} />
      <div className="flex flex-col gap-3 md:gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-2xl md:text-3xl font-bold tracking-tight">Subscriptions</h2>
          <p className="text-sm md:text-base text-muted-foreground">Track and manage your recurring subscriptions.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Tabs value={viewMode} onValueChange={(v) => setViewMode(v as 'list' | 'calendar')} className="mr-2">
            <TabsList>
              <TabsTrigger value="list" data-testid="view-list-tab" className="min-h-[36px]">List</TabsTrigger>
              <TabsTrigger value="calendar" data-testid="view-calendar-tab" className="min-h-[36px]">
                <CalendarDays className="mr-1.5 h-3.5 w-3.5" /> Calendar
              </TabsTrigger>
            </TabsList>
          </Tabs>
          <Button size="sm" onClick={() => { setEditingSub(null); setFormOpen(true); }}>
            <Plus className="mr-2 h-4 w-4" /> Add Subscription
          </Button>
        </div>
      </div>

      {/* Summary Cards — T114-F9-j | T125-F4-d glass-panel */}
      <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
        <Card className="bg-background/80 backdrop-blur-md border-border/50">
          <CardHeader className="pb-2">
            <CardDescription>Monthly Commitment</CardDescription>
            <CardTitle className="truncate text-lg sm:text-2xl">{fmt(summary?.totalMonthly ?? monthlyTotal)}</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            {summary ? `${fmt(summary.totalAnnual)}/year` : 'Total of active subscriptions'}
          </CardContent>
        </Card>
        <Card className="bg-background/80 backdrop-blur-md border-border/50">
          <CardHeader className="pb-2">
            <CardDescription>Next Renewal</CardDescription>
            <CardTitle className="truncate text-lg sm:text-2xl">
              {summary?.upcomingRenewals?.[0]?.nextBillingDate
                ? new Date(summary.upcomingRenewals[0].nextBillingDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
                : nextRenewal?.nextBillingDate
                  ? new Date(nextRenewal.nextBillingDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
                  : '—'}
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground flex items-center gap-1.5">
            <CalendarDays className="h-3.5 w-3.5" />
            {summary?.upcomingRenewals?.[0]?.name ?? (nextRenewal ? nextRenewal.merchantName : 'No upcoming renewals')}
          </CardContent>
        </Card>
        <Card className="bg-background/80 backdrop-blur-md border-border/50">
          <CardHeader className="pb-2">
            <CardDescription>Active</CardDescription>
            <CardTitle className="truncate text-lg sm:text-2xl">{summary?.activeCount ?? statusCounts.active}</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            {summary ? `${summary.pausedCount} paused` : 'Active subscriptions'}
          </CardContent>
        </Card>
        <Card className={`bg-background/80 backdrop-blur-md border-border/50 ${statusCounts.detected > 0 ? 'border-blue-400 dark:border-blue-600' : ''}`}>
          <CardHeader className="pb-2">
            <CardDescription>Detected</CardDescription>
            <CardTitle className="truncate text-lg sm:text-2xl">{statusCounts.detected}</CardTitle>
          </CardHeader>
          <CardContent>
            {statusCounts.detected > 0 ? (
              <button
                type="button"
                className="text-sm text-blue-600 dark:text-blue-400 font-medium flex items-center gap-1 hover:underline"
                onClick={() => setStatusFilter('detected')}
              >
                Review now <ArrowRight className="h-3.5 w-3.5" />
              </button>
            ) : (
              <span className="text-sm text-muted-foreground">No pending detections</span>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Calendar View — T114-F9-l */}
      {viewMode === 'calendar' && (
        <SubscriptionCalendar
          onSubscriptionClick={(subId) => {
            const sub = subscriptions.find((s) => s._id === subId);
            if (sub) openDetail(sub);
          }}
        />
      )}

      {/* List View */}
      {viewMode === 'list' && (
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>All Subscriptions</CardTitle>
              <CardDescription>Manage your recurring subscriptions.</CardDescription>
            </div>
          </div>
          {/* Status filter tabs — T114-F9-i */}
          <div className="overflow-x-auto -mx-1 px-1 mt-2">
            <Tabs value={statusFilter} onValueChange={(v) => { setStatusFilter(v as StatusFilter); setSelectedIds(new Set()); }}>
              <TabsList className="w-full md:w-auto">
                {(['all', 'active', 'paused', 'detected', 'cancelled'] as StatusFilter[]).map((tab) => (
                  <TabsTrigger key={tab} value={tab} className="capitalize gap-1.5 min-h-[36px]">
                    {tab}
                    <Badge variant="secondary" className="ml-1 text-[10px] px-1.5 py-0 min-w-[1.25rem] text-center">
                      {statusCounts[tab]}
                    </Badge>
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </div>
          <div className="relative mt-2">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search subscriptions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
        </CardHeader>
        <CardContent>{renderContent()}</CardContent>
      </Card>
      )}

      <SubscriptionForm
        open={formOpen}
        onOpenChange={(open) => {
          if (!open) setEditingSub(null);
          setFormOpen(open);
        }}
        onSubmit={handleFormSubmit}
        subscription={editingSub}
        isSubmitting={isSubmitting}
      />

      <SubscriptionDetailSheet
        subscription={detailSub}
        open={detailSheetOpen}
        onOpenChange={setDetailSheetOpen}
        onEdit={(sub) => { setEditingSub(sub); setFormOpen(true); }}
        onDelete={(id) => { const sub = subscriptions.find(s => s._id === id); if (sub) setSubToDelete(sub); }}
        onPause={(id) => { dispatch(pauseSubscription(id)).then(() => dispatch(fetchSubscriptionSummary())); toast({ title: 'Subscription paused' }); }}
        onResume={(id) => { handleResume(id); }}
        onCancel={(id) => { handleCancel(id); }}
        onConfirmPayment={(id) => { handleConfirmPayment(id); }}
        onViewTransactions={(sub) => { openTransactions(sub); }}
        onUnlink={(id) => { dispatch(updateSubscription({ id, data: { templateId: undefined } })).then(() => dispatch(fetchSubscriptions())); toast({ title: 'Template unlinked' }); }}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={!!subToDelete} onOpenChange={(open) => !open && setSubToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Delete subscription
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{subToDelete?.merchantName ?? 'this subscription'}</strong>? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete Subscription
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Cancel Confirmation — T114-F9-l */}
      <AlertDialog open={bulkCancelOpen} onOpenChange={setBulkCancelOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Cancel {selectedIds.size} subscription{selectedIds.size > 1 ? 's' : ''}
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to cancel the selected subscription{selectedIds.size > 1 ? 's' : ''}? This will stop all future billing and cannot be easily reversed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Active</AlertDialogCancel>
            <AlertDialogAction onClick={handleBulkCancel} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Cancel Subscription{selectedIds.size > 1 ? 's' : ''}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Transaction Preview Dialog — draft mode confirm flow */}
      <TransactionPreviewDialog
        open={!!previewSubId}
        onOpenChange={(open) => { if (!open) setPreviewSubId(null); }}
        subscriptionId={previewSubId}
        onConfirm={() => {
          if (previewSubId) {
            executeConfirmPayment(previewSubId);
            setPreviewSubId(null);
          }
        }}
      />

      {/* Catch-up billing wizard — shown after resuming a subscription with missed cycles */}
      <CatchUpBillingWizard
        open={catchUpState !== null}
        onClose={() => setCatchUpState(null)}
        subscriptionName={catchUpState?.subscriptionName ?? ''}
        missedCycles={catchUpState?.missedCycles ?? []}
        truncated={catchUpState?.truncated ?? false}
        onConfirm={handleCatchUpConfirm}
      />

    </div>
  );
}
